var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from 'react';
import { Box, Button, IconButton, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ListAndView from './elements/ListAndView';
import FieldListToolbar from './field/FieldListToolbar';
import OpenStreetMaps from './map/OpenStreetMaps';
import ListField from './field/List';
import AddFieldWithCompanyDialog from './field/AddWithCompany';
import FieldInfoPanel from './field/FieldInfo';
import AgrochemBlock from './field/AgrochemBlock';
import axios from 'axios';
import DialogYesNo from './elements/DialodYesNo';
import WeatherOverviewBlock from './field/WeatherOverviewBlock';
import ForecastBlock from './field/ForecastBlock';
import RightMapPanel from './field/RightMapPanel';
import SpectralDashboardView, { BUCKET_COLORS_BY_TYPE, BUCKET_LABELS_BY_TYPE, BUCKET_RANGES_BY_TYPE } from './field/SpectralDashboardView';
import { spectralLegendTitle } from './field/spectralPeriod';
import FertilityMapView from './field/FertilityMapView';
import FertilizationDashboardView from './field/FertilizationDashboardView';
import TaskMapEmptyView from './field/TaskMapEmptyView';
import useToken from '../services/token';
import { canCreateRecords } from '../services/permissions';
import { useOnboarding } from './onboarding/OnboardingProvider';
import { taskMapsAvailable } from './field/taskMapData';
const formatSharePercent = (percent) => {
    const value = Number(percent);
    return value > 0 && value < 0.0005 ? '<0.001' : value.toFixed(3);
};
const Fields = (_a) => {
    var _b, _c, _d, _e;
    var props = __rest(_a, []);
    const { registerSafeAction } = useOnboarding();
    const maxNameLength = 43;
    const [field, setField] = React.useState(null);
    const selectedFieldRef = React.useRef(field);
    selectedFieldRef.current = field;
    const [geometryRevisions, setGeometryRevisions] = React.useState({});
    const [season, setSeason] = React.useState(null);
    const [plotId, setPlotId] = React.useState(null);
    const [planningState, setPlanningState] = React.useState(null);
    const [planningError, setPlanningError] = React.useState(null);
    const [addWithCompanyDialog, setAddWithCompanyDialog] = React.useState(false);
    const [activeLayer, setActiveLayer] = React.useState(null);
    const [layerMapUrl, setLayerMapUrl] = React.useState(null);
    const planningContext = planningState && planningState.fieldId === (field === null || field === void 0 ? void 0 : field.id) && planningState.season === season ? planningState.context : null;
    const tasksAvailable = taskMapsAvailable(planningContext, season);
    React.useEffect(() => {
        const controller = new AbortController();
        setPlanningState(null);
        setPlanningError(null);
        if (!field || season === null)
            return () => controller.abort();
        axios.get(`/fertilizer/planning-context/${field.id}/${season}`, {
            signal: controller.signal, headers: { Authorization: `Bearer ${props.token}` },
        }).then((response) => {
            if (!controller.signal.aborted)
                setPlanningState({ fieldId: field.id, season, context: response.data });
        }).catch(() => {
            if (!controller.signal.aborted)
                setPlanningError('Не удалось проверить доступность карт-заданий. Обновите страницу.');
        });
        return () => controller.abort();
    }, [field === null || field === void 0 ? void 0 : field.id, season, props.token]);
    React.useEffect(() => {
        if (!tasksAvailable && (activeLayer === null || activeLayer === void 0 ? void 0 : activeLayer.kind) === 'task') {
            setLayerMapUrl(null);
        }
    }, [tasksAvailable, activeLayer === null || activeLayer === void 0 ? void 0 : activeLayer.kind]);
    const [spectralRaster, setSpectralRaster] = React.useState(null);
    const [spectralLegend, setSpectralLegend] = React.useState(null);
    const [spectralSharesOpen, setSpectralSharesOpen] = React.useState(false);
    const [spectralRangesOpen, setSpectralRangesOpen] = React.useState(false);
    const [deleteDialog, setDeleteDialog] = React.useState(false);
    const [relations, setRelations] = React.useState([]);
    const map = React.useRef(null);
    const list = React.useRef(null);
    const [panelHeight, setPanelHeight] = React.useState(300);
    const [spectralPanelHeight, setSpectralPanelHeight] = React.useState(275);
    const [spectralPanelMaxHeight, setSpectralPanelMaxHeight] = React.useState(560);
    const resizingRef = React.useRef(false);
    const [bottomTab, setBottomTab] = React.useState(0);
    const bottomTabRef = React.useRef(bottomTab);
    bottomTabRef.current = bottomTab;
    // По умолчанию скрыта (тонкая полоска, без подписей) — до первого
    // выбора сезона. Аналог leftCollapsed/rightCollapsed, но с
    // противоположным дефолтом, т.к. левая/правая по умолчанию открыты.
    const [bottomCollapsed, setBottomCollapsed] = React.useState(true);
    const bottomCollapsedRef = React.useRef(bottomCollapsed);
    bottomCollapsedRef.current = bottomCollapsed;
    const [leftPanelWidth, setLeftPanelWidth] = React.useState(260);
    const [rightPanelWidth, setRightPanelWidth] = React.useState(240);
    const [leftCollapsed, setLeftCollapsed] = React.useState(false);
    const [rightCollapsed, setRightCollapsed] = React.useState(false);
    const rightCollapsedRef = React.useRef(rightCollapsed);
    rightCollapsedRef.current = rightCollapsed;
    React.useEffect(() => {
        if (!field)
            return;
        return registerSafeAction('field-expand-right', () => {
            const previous = rightCollapsedRef.current;
            rightCollapsedRef.current = false;
            setRightCollapsed(false);
            return () => {
                if (rightCollapsedRef.current !== false)
                    return;
                rightCollapsedRef.current = previous;
                setRightCollapsed(previous);
            };
        });
    }, [field === null || field === void 0 ? void 0 : field.id, registerSafeAction]);
    React.useEffect(() => {
        if (!field || season === null || activeLayer !== null)
            return;
        return registerSafeAction('field-expand-bottom', () => {
            const previous = bottomCollapsedRef.current;
            bottomCollapsedRef.current = false;
            setBottomCollapsed(false);
            return () => {
                if (bottomCollapsedRef.current !== false)
                    return;
                bottomCollapsedRef.current = previous;
                setBottomCollapsed(previous);
            };
        });
    }, [field === null || field === void 0 ? void 0 : field.id, season, activeLayer, registerSafeAction]);
    React.useEffect(() => {
        if (!field || season === null || activeLayer !== null || bottomCollapsed)
            return;
        const registerTab = (key, target) => registerSafeAction(key, () => {
            const previous = bottomTabRef.current;
            bottomTabRef.current = target;
            setBottomTab(target);
            return () => {
                if (bottomTabRef.current !== target)
                    return;
                bottomTabRef.current = previous;
                setBottomTab(previous);
            };
        });
        const unregister = [
            registerTab('field-show-weather', 0),
            registerTab('field-show-forecast', 1),
            registerTab('field-show-agrochem', 2),
        ];
        return () => unregister.forEach((dispose) => dispose());
    }, [field === null || field === void 0 ? void 0 : field.id, season, activeLayer, bottomCollapsed, registerSafeAction]);
    // Обе панели делят общий бюджет ширины — при ресайзе одной, если она
    // "заезжает" на территорию другой, вторая реально сужается в реальном
    // времени (а не просто блокирует рост первой).
    const MIN_LEFT = 170;
    const MIN_RIGHT = 170;
    const MIN_MAP_GAP = 150;
    const resizeLeft = (candidate) => {
        const totalBudget = window.innerWidth - MIN_MAP_GAP;
        const rightMin = rightCollapsed ? 28 : MIN_RIGHT;
        const newLeft = Math.min(Math.max(candidate, MIN_LEFT), totalBudget - rightMin);
        setLeftPanelWidth(newLeft);
        if (!rightCollapsed) {
            const remainingForRight = totalBudget - newLeft;
            setRightPanelWidth((prev) => Math.min(prev, Math.max(MIN_RIGHT, remainingForRight)));
        }
    };
    const resizeRight = (candidate) => {
        const totalBudget = window.innerWidth - MIN_MAP_GAP;
        const leftMin = leftCollapsed ? 28 : MIN_LEFT;
        const newRight = Math.min(Math.max(candidate, MIN_RIGHT), totalBudget - leftMin);
        setRightPanelWidth(newRight);
        if (!leftCollapsed) {
            const remainingForLeft = totalBudget - newRight;
            setLeftPanelWidth((prev) => Math.min(prev, Math.max(MIN_LEFT, remainingForLeft)));
        }
    };
    const startResize = (e) => {
        e.preventDefault();
        resizingRef.current = true;
        document.body.style.cursor = 'row-resize';
        document.body.style.userSelect = 'none';
    };
    const updateSpectralPanelLayout = React.useCallback((height, maxHeight) => {
        var _a, _b, _c;
        setSpectralPanelMaxHeight(maxHeight);
        const availableMax = Math.round(((_c = (_b = (_a = bottomOverlayRef.current) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.clientHeight) !== null && _c !== void 0 ? _c : window.innerHeight) * 0.7);
        setSpectralPanelHeight(Math.min(height, availableMax));
    }, []);
    const { parseJwt } = useToken();
    const role = React.useMemo(() => { var _a, _b; return (_b = (_a = parseJwt(props.token)) === null || _a === void 0 ? void 0 : _a.role) !== null && _b !== void 0 ? _b : null; }, [props.token]);
    const canCreateField = canCreateRecords(role);
    React.useEffect(() => {
        const onMove = (e) => {
            var _a, _b, _c, _d, _e, _f;
            if (!resizingRef.current)
                return;
            const containerBottom = (_c = (_b = (_a = bottomOverlayRef.current) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().bottom) !== null && _c !== void 0 ? _c : window.innerHeight;
            const newHeight = containerBottom - e.clientY;
            // Панель может стать компактнее: график и фильтр делят доступную
            // высоту, а таймлайн и строка экспорта имеют компактные размеры.
            const availableMax = Math.round(((_f = (_e = (_d = bottomOverlayRef.current) === null || _d === void 0 ? void 0 : _d.parentElement) === null || _e === void 0 ? void 0 : _e.clientHeight) !== null && _f !== void 0 ? _f : window.innerHeight) * 0.7);
            const max = (activeLayer === null || activeLayer === void 0 ? void 0 : activeLayer.kind) === 'analytical' ? Math.min(availableMax, spectralPanelMaxHeight) : availableMax;
            const min = Math.min(150, max);
            const height = Math.min(max, Math.max(min, newHeight));
            if ((activeLayer === null || activeLayer === void 0 ? void 0 : activeLayer.kind) === 'analytical')
                setSpectralPanelHeight(height);
            else
                setPanelHeight(height);
        };
        const onUp = () => {
            resizingRef.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, [activeLayer === null || activeLayer === void 0 ? void 0 : activeLayer.kind, spectralPanelMaxHeight]);
    React.useEffect(() => {
        if (map.current) {
            if (field) {
                let latlng = { lat: 49.9714, lng: 82.6059 };
                if (field.latitude_center && field.longitude_center) {
                    if (field.latitude_center > 1 && field.longitude_center > 1) {
                        latlng = {
                            lat: field === null || field === void 0 ? void 0 : field.latitude_center,
                            lng: field === null || field === void 0 ? void 0 : field.longitude_center
                        };
                    }
                }
                map.current.setUrl(`/field/get/${field === null || field === void 0 ? void 0 : field.id}`, latlng);
            }
            else {
                // Поле не выбрано (закрыли дашборд) — снимаем полигон,
                // возвращаем карту к виду по умолчанию.
                map.current.setUrl('', { lat: 49.9714, lng: 82.6059 });
            }
        }
    }, [field]);
    React.useEffect(() => {
        if (map.current) {
            if (activeLayer && layerMapUrl && field) {
                let latlng = { lat: 49.9714, lng: 82.6059 };
                if (field.latitude_center && field.longitude_center) {
                    if (field.latitude_center > 1 && field.longitude_center > 1) {
                        latlng = { lat: field.latitude_center, lng: field.longitude_center };
                    }
                }
                map.current.setUrl(layerMapUrl, latlng);
            }
            else if (field) {
                let latlng = { lat: 49.9714, lng: 82.6059 };
                if (field.latitude_center && field.longitude_center) {
                    if (field.latitude_center > 1 && field.longitude_center > 1) {
                        latlng = { lat: field.latitude_center, lng: field.longitude_center };
                    }
                }
                map.current.setUrl(`/field/get/${field.id}`, latlng);
            }
        }
    }, [layerMapUrl, activeLayer]);
    React.useEffect(() => {
        setActiveLayer(null);
        setLayerMapUrl(null);
        setSpectralRaster(null);
        setSpectralLegend(null);
        // Авто-открытие при выборе сезона — тот же принцип, что уже
        // работает у левой/правой панели (они открыты по умолчанию).
        setBottomCollapsed(false);
    }, [field === null || field === void 0 ? void 0 : field.id, season]);
    const deleteListItem = () => {
        axios.delete(`/field/delete/${field === null || field === void 0 ? void 0 : field.id}`, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${props.token}`
            }
        })
            .then((responce) => {
            var _a, _b;
            if (responce.data) {
                if (responce.data.result) {
                    setField(null);
                    setSeason(null);
                    setPlotId(null);
                    setDeleteDialog(false);
                    (_a = map.current) === null || _a === void 0 ? void 0 : _a.invalidateSize();
                    (_b = list.current) === null || _b === void 0 ? void 0 : _b.update();
                }
            }
        });
    };
    const updateListItems = () => {
        var _a;
        (_a = list.current) === null || _a === void 0 ? void 0 : _a.update();
    };
    const refreshCurrentField = () => {
        updateListItems();
        if (map.current && field) {
            let latlng = { lat: 49.9714, lng: 82.6059 };
            if (field.latitude_center && field.longitude_center) {
                if (field.latitude_center > 1 && field.longitude_center > 1) {
                    latlng = {
                        lat: field.latitude_center,
                        lng: field.longitude_center
                    };
                }
            }
            map.current.setUrl(`/field/get/${field.id}`, latlng);
        }
    };
    const handleGeometryUpdated = (fieldId) => {
        var _a;
        // Saving may finish after switching fields. Invalidate only the edited
        // field, without changing the chosen layer, season or panel state.
        setGeometryRevisions((previous) => { var _a; return (Object.assign(Object.assign({}, previous), { [fieldId]: ((_a = previous[fieldId]) !== null && _a !== void 0 ? _a : 0) + 1 })); });
        updateListItems();
        const currentField = selectedFieldRef.current;
        if ((currentField === null || currentField === void 0 ? void 0 : currentField.id) !== fieldId)
            return;
        setLayerMapUrl(null);
        setSpectralRaster(null);
        setSpectralLegend(null);
        const center = { lat: currentField.latitude_center || 49.9714, lng: currentField.longitude_center || 82.6059 };
        (_a = map.current) === null || _a === void 0 ? void 0 : _a.setUrl(`/field/get/${fieldId}`, center);
    };
    const deleteField = (x, relations) => {
        setRelations(relations);
        setDeleteDialog(true);
    };
    // Сбрасываем layerMapUrl синхронно вместе со сменой activeLayer —
    // иначе при переключении коллекции (Sentinel → Landsat) на один кадр
    // возникает окно, где activeLayer уже новый, а layerMapUrl ещё старый,
    // и эффект ниже повторно применяет устаревший URL снимка с предыдущей
    // коллекции поверх новой — отсюда "битые" пиксельные остатки на карте.
    const handleSelectLayer = (layer) => {
        setLayerMapUrl(null);
        setSpectralRaster(null);
        setSpectralLegend(null);
        setBottomCollapsed(false);
        setActiveLayer(layer);
    };
    // Реальная высота нижнего оверлея измеряется через ResizeObserver, а не
    // оценивается числом — так боковые панели совпадают с ним пиксель в
    // пиксель независимо от border/padding внутри строки табов.
    const bottomOverlayRef = React.useRef(null);
    const [bottomOverlayHeight, setBottomOverlayHeight] = React.useState(0);
    React.useLayoutEffect(() => {
        const node = bottomOverlayRef.current;
        if (!node) {
            setBottomOverlayHeight(0);
            return;
        }
        const observer = new ResizeObserver((entries) => {
            setBottomOverlayHeight(entries[0].contentRect.height);
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, [field === null || field === void 0 ? void 0 : field.id, season, activeLayer]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Stack, { spacing: 0, sx: { padding: '0px', height: 'auto', minHeight: 0, minWidth: 0, flex: '1 1 0%', pt: '0px', overflow: 'hidden' } },
            React.createElement(ListAndView, { leftWidth: 220, resizable: true, left: React.createElement(Box, { sx: { height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' } },
                    React.createElement(Box, { "data-onboarding-anchor": "fields-list", sx: {
                            flex: '1 1 0',
                            minHeight: 0,
                            overflowY: 'auto',
                            scrollbarGutter: 'stable',
                            "&::-webkit-scrollbar": {
                                width: 8
                            },
                            "&::-webkit-scrollbar-track": {
                                backgroundColor: "#F1F1F1"
                            },
                            "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "#C1C1C1",
                                borderRadius: 2
                            }
                        } },
                        React.createElement(FieldListToolbar, { canCreate: canCreateField, onAddOnMap: () => setAddWithCompanyDialog(true), onRefresh: updateListItems }),
                        React.createElement(ListField, { ref: list, token: props.token, progressItems: [], showWarning: true, select: (f, s, p) => { setField(f); setSeason(s); setPlotId(p); } }))), view: React.createElement(Box, { "data-onboarding-anchor": "fields-map", "data-onboarding-state": field === null || season === null ? 'empty' : undefined, sx: { position: 'relative', height: '100%', minHeight: 0 } },
                    React.createElement(Box, { sx: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0, overflow: 'hidden' } },
                        React.createElement(OpenStreetMaps, { ref: map, token: props.token, url: null, spectralRaster: spectralRaster, showStantions: `stantions`, draw: layerMapUrl !== null, createBoundExtend: layerMapUrl !== null, scipBorder: (activeLayer === null || activeLayer === void 0 ? void 0 : activeLayer.kind) === 'task' && activeLayer.type !== 'fertility', item: field == null ? 0 : field.id })),
                    spectralLegend && (activeLayer === null || activeLayer === void 0 ? void 0 : activeLayer.kind) === 'analytical' && (React.createElement(Box, { sx: {
                            position: 'absolute', top: 12,
                            left: leftCollapsed ? 40 : leftPanelWidth + 12,
                            right: rightCollapsed ? 40 : rightPanelWidth + 12,
                            zIndex: 1000, display: 'flex', justifyContent: 'flex-end',
                            pointerEvents: 'none'
                        } },
                        React.createElement(Box, { sx: {
                                width: 'min(440px, 100%)', maxHeight: 'calc(100% - 24px)', overflowY: 'auto', px: 1.5, py: 1,
                                borderRadius: 1.5, backgroundColor: 'rgba(255,255,255,0.96)',
                                border: '1px solid rgba(0,0,0,0.08)',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.16)',
                                backdropFilter: 'blur(4px)', pointerEvents: 'auto'
                            } },
                            React.createElement(Stack, { direction: "row", spacing: 1, alignItems: "center", flexWrap: "nowrap" },
                                React.createElement(Typography, { variant: "subtitle2", fontWeight: 700, noWrap: true, title: spectralLegendTitle(spectralLegend), sx: { flex: 1, minWidth: 0 } }, spectralLegendTitle(spectralLegend)),
                                React.createElement(Button, { size: "small", onClick: () => setSpectralSharesOpen((open) => !open), endIcon: spectralSharesOpen ? React.createElement(ExpandLessIcon, null) : React.createElement(ExpandMoreIcon, null), sx: { minWidth: 0, flexShrink: 0, px: 0.75, textTransform: 'none' } }, "\u0414\u043E\u043B\u0438"),
                                React.createElement(Button, { size: "small", onClick: () => setSpectralRangesOpen((open) => !open), endIcon: spectralRangesOpen ? React.createElement(ExpandLessIcon, null) : React.createElement(ExpandMoreIcon, null), sx: { minWidth: 0, flexShrink: 0, px: 0.75, textTransform: 'none' } }, "\u0414\u0438\u0430\u043F\u0430\u0437\u043E\u043D\u044B")),
                            spectralSharesOpen && (React.createElement(Box, { sx: { mt: 0.75, pt: 0.75, borderTop: '1px solid', borderColor: 'divider', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(185px, 1fr))', columnGap: 1.5, rowGap: 0.5 } },
                                Object.entries(spectralLegend.percents)
                                    .filter(([bucket]) => bucket !== '100' && bucket !== '100.0')
                                    .sort(([a], [b]) => parseFloat(a) - parseFloat(b))
                                    .map(([bucket, percent]) => {
                                    var _a, _b, _c;
                                    return (React.createElement(Stack, { key: bucket, direction: "row", spacing: 0.65, alignItems: "center", minWidth: 0 },
                                        React.createElement(Box, { sx: { width: 11, height: 11, flexShrink: 0, borderRadius: '50%', border: '1px solid rgba(0,0,0,0.18)', bgcolor: (_b = (_a = spectralLegend.colors[bucket]) !== null && _a !== void 0 ? _a : BUCKET_COLORS_BY_TYPE[spectralLegend.type][bucket]) !== null && _b !== void 0 ? _b : '#999' } }),
                                        React.createElement(Typography, { variant: "caption", sx: { minWidth: 0, flex: 1 } }, (_c = BUCKET_LABELS_BY_TYPE[spectralLegend.type][bucket]) !== null && _c !== void 0 ? _c : bucket),
                                        React.createElement(Typography, { variant: "caption", fontWeight: 700 },
                                            formatSharePercent(percent),
                                            "%")));
                                }),
                                ((_b = spectralLegend.noDataPercent) !== null && _b !== void 0 ? _b : 0) > 0 && (React.createElement(Stack, { direction: "row", spacing: 0.65, alignItems: "center", minWidth: 0 },
                                    React.createElement(Box, { sx: { width: 11, height: 11, flexShrink: 0, borderRadius: '50%', border: '1px solid rgba(0,0,0,0.18)', bgcolor: (_c = spectralLegend.noDataColor) !== null && _c !== void 0 ? _c : '#b0bec5' } }),
                                    React.createElement(Typography, { variant: "caption", sx: { minWidth: 0, flex: 1 } }, "\u041D\u0435\u0442 \u043F\u0440\u0438\u0433\u043E\u0434\u043D\u044B\u0445 \u0434\u0430\u043D\u043D\u044B\u0445 (\u043E\u0442 \u043F\u043B\u043E\u0449\u0430\u0434\u0438 \u043F\u043E\u043B\u044F)"),
                                    React.createElement(Typography, { variant: "caption", fontWeight: 700 },
                                        formatSharePercent((_d = spectralLegend.noDataPercent) !== null && _d !== void 0 ? _d : 0),
                                        "%"))))),
                            spectralRangesOpen && (React.createElement(Box, { sx: { mt: 0.75, pt: 0.75, borderTop: '1px solid', borderColor: 'divider', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(185px, 1fr))', columnGap: 1.5, rowGap: 0.5 } }, Object.entries(BUCKET_RANGES_BY_TYPE[spectralLegend.type])
                                .sort(([a], [b]) => parseFloat(a) - parseFloat(b))
                                .map(([bucket, range]) => {
                                var _a, _b, _c;
                                return (React.createElement(Stack, { key: bucket, direction: "row", spacing: 0.65, alignItems: "center", minWidth: 0 },
                                    React.createElement(Box, { sx: { width: 11, height: 11, flexShrink: 0, borderRadius: '50%', border: '1px solid rgba(0,0,0,0.18)', bgcolor: (_b = (_a = spectralLegend.colors[bucket]) !== null && _a !== void 0 ? _a : BUCKET_COLORS_BY_TYPE[spectralLegend.type][bucket]) !== null && _b !== void 0 ? _b : '#999' } }),
                                    React.createElement(Typography, { variant: "caption", sx: { minWidth: 0, flex: 1 } }, (_c = BUCKET_LABELS_BY_TYPE[spectralLegend.type][bucket]) !== null && _c !== void 0 ? _c : bucket),
                                    React.createElement(Typography, { variant: "caption", color: "text.secondary", sx: { whiteSpace: 'nowrap' } }, range)));
                            })))))),
                    field !== null && (React.createElement(Box, { sx: { position: 'absolute', top: 0, left: 0, bottom: `${bottomOverlayHeight}px`, zIndex: 1200, overflow: 'hidden' } },
                        React.createElement(FieldInfoPanel, { token: props.token, id: field.id, season: season, delete: deleteField, onUpdated: refreshCurrentField, onGeometryUpdated: handleGeometryUpdated, collapsed: leftCollapsed, onToggleCollapse: () => setLeftCollapsed(!leftCollapsed), width: leftPanelWidth, onResize: resizeLeft }))),
                    field !== null && (React.createElement(Box, { "data-onboarding-anchor": "field-maps-panel", sx: { position: 'absolute', top: 0, right: 0, bottom: `${bottomOverlayHeight}px`, zIndex: 1200, overflow: 'hidden' } },
                        React.createElement(RightMapPanel, { token: props.token, activeLayer: activeLayer, onSelect: handleSelectLayer, collapsed: rightCollapsed, onToggleCollapse: () => setRightCollapsed(!rightCollapsed), width: rightPanelWidth, onResize: resizeRight, planningError: planningError }))),
                    field !== null && season !== null && (React.createElement(Box, { ref: bottomOverlayRef, sx: {
                            position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 1100,
                            display: 'flex', flexDirection: 'column',
                            maxHeight: '70%', minHeight: 0, overflow: 'hidden',
                            backgroundColor: '#fff', boxShadow: '0 -2px 6px rgba(0,0,0,0.2)'
                        } },
                        !bottomCollapsed && (React.createElement(Box, { onMouseDown: startResize, sx: {
                                height: '6px', flexShrink: 0, cursor: 'row-resize',
                                backgroundColor: 'transparent',
                                transition: 'background-color 0.15s',
                                '&:hover': { backgroundColor: 'rgba(18, 113, 71, 0.35)' }
                            } })),
                        (activeLayer === null || activeLayer === void 0 ? void 0 : activeLayer.kind) === 'analytical' && plotId !== null && (React.createElement(Box, { sx: { height: spectralPanelHeight, minHeight: 0, flexShrink: 1, overflow: 'hidden', display: bottomCollapsed ? 'none' : 'block' } },
                            React.createElement(SpectralDashboardView, { token: props.token, type: activeLayer.type, plotId: plotId, field: field, geometryRevision: (_e = geometryRevisions[field.id]) !== null && _e !== void 0 ? _e : 0, season: season, onMapUrlChange: setLayerMapUrl, onRasterChange: setSpectralRaster, onLegendChange: setSpectralLegend, onPanelLayoutChange: updateSpectralPanelLayout, onCollapse: () => setBottomCollapsed(true), onClose: () => { setSpectralLegend(null); setSpectralRaster(null); setActiveLayer(null); } }))),
                        !tasksAvailable && (activeLayer === null || activeLayer === void 0 ? void 0 : activeLayer.kind) === 'task' && (React.createElement(Box, { "data-field-panel-scroll": "task-empty", sx: { maxHeight: panelHeight, minHeight: 0, flexShrink: 1, overflow: 'auto', display: bottomCollapsed ? 'none' : 'block' } },
                            React.createElement(TaskMapEmptyView, { type: activeLayer.type, season: season, planningContext: planningContext, error: planningError, onCollapse: () => setBottomCollapsed(true), onClose: () => setActiveLayer(null) }))),
                        tasksAvailable && (activeLayer === null || activeLayer === void 0 ? void 0 : activeLayer.kind) === 'task' && activeLayer.type === 'fertility' && (React.createElement(Box, { "data-field-panel-scroll": "fertility", sx: { height: panelHeight, minHeight: 0, flexShrink: 1, overflow: 'auto', display: bottomCollapsed ? 'none' : 'block' } },
                            React.createElement(FertilityMapView, { key: `fertility-${field.id}-${season}`, token: props.token, field: field, season: season, onMapUrlChange: setLayerMapUrl, onCollapse: () => setBottomCollapsed(true), onClose: () => setActiveLayer(null) }))),
                        tasksAvailable && (activeLayer === null || activeLayer === void 0 ? void 0 : activeLayer.kind) === 'task' && activeLayer.type !== 'fertility' && (React.createElement(Box, { "data-field-panel-scroll": "fertilization", sx: { height: panelHeight, minHeight: 0, flexShrink: 1, overflow: 'auto', display: bottomCollapsed ? 'none' : 'block' } },
                            React.createElement(FertilizationDashboardView, { key: `fertilization-${field.id}-${season}`, token: props.token, nutrient: activeLayer.type, field: field, season: season, planningContext: planningContext, onMapUrlChange: setLayerMapUrl, onCollapse: () => setBottomCollapsed(true), onClose: () => setActiveLayer(null) }))),
                        activeLayer !== null && bottomCollapsed && (React.createElement(Stack, { direction: "row", alignItems: "center", justifyContent: "space-between", onClick: () => setBottomCollapsed(false), sx: { height: 30, px: 1.5, flexShrink: 0, cursor: 'pointer', bgcolor: '#fff', borderTop: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: '#f7f9f7' } } },
                            React.createElement(Typography, { variant: "caption", fontWeight: 700 },
                                activeLayer.kind === 'analytical' ? activeLayer.type.toUpperCase() : activeLayer.type === 'fertility' ? 'Карта плодородия' : 'Карта-задание',
                                " \u00B7 \u0421\u0435\u0437\u043E\u043D ",
                                season),
                            React.createElement(Tooltip, { title: "\u0420\u0430\u0437\u0432\u0435\u0440\u043D\u0443\u0442\u044C \u043D\u0438\u0436\u043D\u044E\u044E \u043F\u0430\u043D\u0435\u043B\u044C" },
                                React.createElement(IconButton, { size: "small", "aria-label": "\u0420\u0430\u0437\u0432\u0435\u0440\u043D\u0443\u0442\u044C \u043D\u0438\u0436\u043D\u044E\u044E \u043F\u0430\u043D\u0435\u043B\u044C" },
                                    React.createElement(ExpandLessIcon, { fontSize: "small", sx: { color: '#2e7d32' } }))))),
                        activeLayer === null && (bottomCollapsed ? (React.createElement(Stack, { direction: "row", alignItems: "center", justifyContent: "center", onClick: () => setBottomCollapsed(false), sx: {
                                height: 22, flexShrink: 0, cursor: 'pointer',
                                backgroundColor: '#f7f9f7',
                                borderTop: '1px solid #e2e8e3',
                                '&:hover': { backgroundColor: '#edf3ee' }
                            } },
                            React.createElement(ExpandLessIcon, { fontSize: "small", sx: { color: '#2e7d32' } }))) : (React.createElement(React.Fragment, null,
                            React.createElement(Stack, { direction: "row", alignItems: "center", sx: { flexShrink: 0, width: '100%', borderTop: '1px solid rgba(255,255,255,0.3)', backgroundColor: 'transparent' } },
                                React.createElement(Tabs, { value: bottomTab, onChange: (_, v) => setBottomTab(v), sx: { flex: 1, minHeight: 40, '& .MuiTab-root': { minHeight: 40, fontSize: 13, textTransform: 'none' } } },
                                    React.createElement(Tab, { "data-onboarding-anchor": "field-weather-tab", label: "\u041C\u0435\u0442\u0435\u043E\u0434\u0430\u043D\u043D\u044B\u0435" }),
                                    React.createElement(Tab, { "data-onboarding-anchor": "field-forecast-tab", label: "\u041F\u0440\u043E\u0433\u043D\u043E\u0437 \u043F\u043E\u0433\u043E\u0434\u044B" }),
                                    React.createElement(Tab, { "data-onboarding-anchor": "field-agrochem-tab", label: "\u0410\u0433\u0440\u043E\u0445\u0438\u043C\u0438\u044F" })),
                                React.createElement(Tooltip, { title: "\u0421\u0432\u0435\u0440\u043D\u0443\u0442\u044C" },
                                    React.createElement(IconButton, { size: "small", sx: { mr: 1 }, onClick: () => setBottomCollapsed(true) },
                                        React.createElement(ExpandMoreIcon, { fontSize: "small" })))),
                            React.createElement(Box, { "data-onboarding-anchor": bottomTab === 0 ? 'field-weather-content' : bottomTab === 1 ? 'field-forecast-content' : 'field-agrochem-content', sx: { height: panelHeight, minHeight: 0, flexShrink: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' } },
                                bottomTab === 0 && React.createElement(WeatherOverviewBlock, { token: props.token, fieldId: field.id, season: season }),
                                bottomTab === 1 && React.createElement(ForecastBlock, { token: props.token, fieldId: field.id }),
                                bottomTab === 2 && React.createElement(AgrochemBlock, { token: props.token, fieldId: field.id, season: season })))))))) })),
        React.createElement(AddFieldWithCompanyDialog, { token: props.token, open: addWithCompanyDialog, maxLength: maxNameLength, update: updateListItems, setState: setAddWithCompanyDialog }),
        React.createElement(DialogYesNo, { title: '\u0423\u0434\u0430\u043B\u0435\u043D\u0438\u0435', question: `Вы хотите удалить поле ${field === null || field === void 0 ? void 0 : field.name} без возможности восстановления?`, open: deleteDialog, description: {
                title: "Будут удалены следующие связанные данные:",
                values: relations
            }, yes: () => deleteListItem(), no: setDeleteDialog })));
};
export default Fields;
